import { defineMessage } from 'react-intl';

import { TestimonialQuoteProps } from '../testimonial-quote';

// put data for default state into hero component file

type TeamType =
	| 'default'
	| 'software'
	| 'product-management'
	| 'marketing'
	| 'project-management'
	| 'design'
	| 'it';

type Team = {
	id: TeamType;
	icon: string;
	heroImage: string;
	title: {
		id: string;
		defaultMessage: string;
	};
	englishLabel: string;
	contentTitle: {
		id: string;
		defaultMessage: string;
	};
	contentText: {
		id: string;
		defaultMessage: string;
	};
	quoteColor: TestimonialQuoteProps['quoteColor'];
	quoteText: {
		id: string;
		defaultMessage: string;
	};
	customerName: {
		id: string;
		defaultMessage: string;
	};
	customerTitle: {
		id: string;
		defaultMessage: string;
	};
};

const TEAM_DATA: Team[] = [
	{
		id: 'software',
		icon: 'https://dam-cdn.atl.orangelogic.com/AssetLink/0tktsadc6a0h85663hv4lb34mml07y0g.svg',
		heroImage:
			'https://dam-cdn.atl.orangelogic.com/AssetLink/1l8b1267j66mc1b334eua3gyam2nggrx.webp',
		title: defineMessage({
			id: 'JiraProductDiscoveryPage.howItWorks.tabs.label.tab1',
			defaultMessage: 'Capture',
		}),
		englishLabel: 'Capture',
		contentTitle: defineMessage({
			id: 'JiraProductDiscoveryPage.howItWorks.tabs.title.tab1',
			defaultMessage: 'Capture ideas and insights',
		}),
		contentText: defineMessage({
			id: 'JiraProductDiscoveryPage.howItWorks.tabs.text.tab1',
			defaultMessage:
				'There’s now a home for all the unstructured work that happens before you start building software. Gather and organize your product opportunities, user feedback, and feature requests all in one place.',
		}),
		quoteColor: 'blue',
		quoteText: defineMessage({
			id: 'JiraProductDiscoveryPage.howItWorks.tabs.quote.text.tab1',
			defaultMessage:
				'Jira Product Discovery is helping us do the right things, in the right order, and at the right time, focusing on work that will deliver on benefits.',
		}),
		customerName: defineMessage({
			id: 'JiraProductDiscoveryPage.howItWorks.tabs.quote.name.tab1',
			defaultMessage: 'John Sayers',
		}),
		customerTitle: defineMessage({
			id: 'JiraProductDiscoveryPage.howItWorks.tabs.quote.title.tab1',
			defaultMessage: 'PMO Lead, Renaissancere',
		}),
	},
	{
		id: 'product-management',
		icon: 'https://dam-cdn.atl.orangelogic.com/AssetLink/kha7lb4n41e6nqbfm6uu8jwym0dumt36.svg',
		heroImage:
			'https://dam-cdn.atl.orangelogic.com/AssetLink/ohh610p3j7iv5hyda53yqh0006v40258.webp',
		title: defineMessage({
			id: 'JiraProductDiscoveryPage.howItWorks.tabs.label.tab2',
			defaultMessage: 'Prioritize',
		}),
		englishLabel: 'Prioritize',
		contentTitle: defineMessage({
			id: 'JiraProductDiscoveryPage.howItWorks.tabs.title.tab2',
			defaultMessage: 'Prioritize – your way',
		}),
		contentText: defineMessage({
			id: 'JiraProductDiscoveryPage.howItWorks.tabs.text.tab2',
			defaultMessage:
				'Flexible views and granular fields let you compare ideas against one another using the prioritization method that works best for you.',
		}),
		quoteColor: 'lime',
		quoteText: defineMessage({
			id: 'JiraProductDiscoveryPage.howItWorks.tabs.quote.text.tab2',
			defaultMessage:
				'Up until Jira Product Discovery, even with other roadmapping tools, we’ve had to use shared spreadsheets and slide presentations to coordinate prioritization in an accessible way. However, within a week of “trying” out Jira Product Discovery, it became clear it was the tool to invest in going forward.',
		}),
		customerName: defineMessage({
			id: 'JiraProductDiscoveryPage.howItWorks.tabs.quote.name.tab2',
			defaultMessage: 'John Daily Jr.',
		}),
		customerTitle: defineMessage({
			id: 'JiraProductDiscoveryPage.howItWorks.tabs.quote.title.tab2',
			defaultMessage: 'Head of Product, Karhoo',
		}),
	},
	{
		id: 'marketing',
		icon: 'https://dam-cdn.atl.orangelogic.com/AssetLink/36m76eu4oo0e46hvd21s744fn11i35q5.svg',
		heroImage:
			'https://dam-cdn.atl.orangelogic.com/AssetLink/2fh1741j5vly80365ofehwwe0qes6b4a.webp',
		title: defineMessage({
			id: 'JiraProductDiscoveryPage.howItWorks.tabs.label.tab3',
			defaultMessage: 'Engage',
		}),
		englishLabel: 'Engage',
		contentTitle: defineMessage({
			id: 'JiraProductDiscoveryPage.howItWorks.tabs.title.tab3',
			defaultMessage: 'Engage and align your teams',
		}),
		contentText: defineMessage({
			id: 'JiraProductDiscoveryPage.howItWorks.tabs.text.tab3',
			defaultMessage:
				'Create and share roadmaps tailored to individual stakeholders – even ones outside your organization – and let them provide feedback via comments, votes, or simple reactions.',
		}),
		quoteColor: 'purple',
		quoteText: defineMessage({
			id: 'JiraProductDiscoveryPage.howItWorks.tabs.quote.text.tab3',
			defaultMessage: 'Jira Product Discovery has revitalized how we think about roadmaps.',
		}),
		customerName: defineMessage({
			id: 'JiraProductDiscoveryPage.howItWorks.tabs.quote.name.tab3',
			defaultMessage: 'Adam Chapman',
		}),
		customerTitle: defineMessage({
			id: 'JiraProductDiscoveryPage.howItWorks.tabs.quote.title.tab3',
			defaultMessage: 'Full Stack Developer, Apex Networks',
		}),
	},
	{
		id: 'project-management',
		icon: 'https://dam-cdn.atl.orangelogic.com/AssetLink/3e2ln2b76w2544cn5633031qjtn2fobe.svg',
		heroImage:
			'https://dam-cdn.atl.orangelogic.com/AssetLink/r1elfv055406ty7x72u4000npq20n46m.webp',
		title: defineMessage({
			id: 'JiraProductDiscoveryPage.howItWorks.tabs.label.tab4',
			defaultMessage: 'Connect',
		}),
		englishLabel: 'Connect',
		contentTitle: defineMessage({
			id: 'JiraProductDiscoveryPage.howItWorks.tabs.title.tab4',
			defaultMessage: 'Connect teams, all in Jira',
		}),
		contentText: defineMessage({
			id: 'JiraProductDiscoveryPage.howItWorks.tabs.text.tab4',
			defaultMessage:
				'Seamlessly connect your ideas to delivery work in Jira so your teams have full context and visibility from discovery to delivery.',
		}),
		quoteColor: 'yellow',
		quoteText: defineMessage({
			id: 'JiraProductDiscoveryPage.howItWorks.tabs.quote.text.tab4',
			defaultMessage:
				'Jira Product Discovery combines everything I was doing in spreadsheets and Powerpoints into one easy-to-navigate tool. Plus everything is added straight into Jira, instead of requiring some third-party tool to integrate.',
		}),
		customerName: defineMessage({
			id: 'JiraProductDiscoveryPage.howItWorks.tabs.quote.name.tab4',
			defaultMessage: 'Abby Kiesling',
		}),
		customerTitle: defineMessage({
			id: 'JiraProductDiscoveryPage.howItWorks.tabs.quote.title.tab4',
			defaultMessage: 'Product Manager, EZ Texting',
		}),
	},
];

export { TEAM_DATA, type Team };
